import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/products";
import {
  ArrowRight,
  Truck,
  PackageOpen,
  PackagePlus,
  CheckCircle2,
  Mail,
  MessageCircle,
  Send,
} from "lucide-react";

const HERO_IMAGE =
  "https://images.pexels.com/photos/11666903/pexels-photo-11666903.jpeg";

const JOBS = [
  {
    slug: "truck-driver",
    icon: Truck,
    code: "01",
    title: "Truck Driver",
    type: "Full-time · Field",
    location: "Kabwe HQ Yard",
    summary:
      "Operate heavy-duty haulage trucks moving copper, cobalt and manganese ore from our Kabwe yard to ports.",
    requirements: [
      "Valid Class CE Zambian driver's licence",
      "Minimum 3 years heavy-vehicle experience",
      "Cross-border experience (TZ / NA / MZ corridors) preferred",
      "Clean driving record and DVLA / RTSA medical clearance",
      "Familiarity with weighbridge tickets and ZRA documentation",
    ],
    responsibilities: [
      "Safe daily haulage between sites and ports",
      "Pre-trip vehicle checks and incident reporting",
      "Adherence to load-secure and tarping protocols",
      "Compliance with road regulations and rest periods",
    ],
  },
  {
    slug: "loader-operator",
    icon: PackagePlus,
    code: "02",
    title: "Loading Operator",
    type: "Full-time · Yard",
    location: "Kabwe HQ Yard",
    summary:
      "Operate loaders, forklifts and bulk-bag handling equipment to load haulage trucks at our Kabwe consolidation yard.",
    requirements: [
      "Forklift / wheel-loader certification",
      "Minimum 2 years yard/loading experience",
      "Familiar with jumbo-bag (1 MT) loading and weighbridge procedure",
      "Physical fit, safety-conscious, team-oriented",
    ],
    responsibilities: [
      "Daily loading of trucks per dispatch schedule",
      "Verifying packing list against weighbridge ticket",
      "Equipment care and pre-shift inspection",
      "Reporting damage or shortage to yard supervisor",
    ],
  },
  {
    slug: "unloader-operator",
    icon: PackageOpen,
    code: "03",
    title: "Offloading & Unloading Operator",
    type: "Full-time · Yard",
    location: "Kabwe HQ Yard",
    summary:
      "Unload inbound material from mine-site trucks at our Kabwe yard, grade and stage for export, ensuring tally and sample accuracy.",
    requirements: [
      "Yard operations / warehouse handling experience",
      "Familiarity with grading and tally procedures",
      "Comfortable lifting and operating manual equipment",
      "Detail-oriented; can read packing lists / delivery notes",
    ],
    responsibilities: [
      "Receiving and tallying inbound deliveries",
      "Staging material by grade for inspection",
      "Sampling assistance for SGS / BV inspectors",
      "Daily stock reconciliation reporting",
    ],
  },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  position: "",
  experience: "",
  location: "",
  message: "",
};

export default function Careers() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const apply = (jobTitle) => {
    setForm((f) => ({ ...f, position: jobTitle }));
    setTimeout(
      () =>
        document
          .getElementById("apply-form")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      40
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const composed =
        `[JOB APPLICATION]\n` +
        `Position: ${form.position || "—"}\n` +
        `Experience: ${form.experience || "—"} years\n` +
        `Preferred Location: ${form.location || "—"}\n\n` +
        `${form.message}`;

      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        message: composed,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      await api.post("/inquiries", payload);
      toast.success("Application received. We'll be in touch shortly.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Validation error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-careers">
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Trucks at our Kabwe yard"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--eel-bg)] via-[var(--eel-bg)]/70 to-[var(--eel-bg)]/35" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
          <p className="label-eyebrow text-[var(--eel-copper)] reveal">
            Careers · {new Date().getFullYear()}
          </p>
          <h1 className="font-editorial text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1] text-white max-w-4xl reveal reveal-delay-1">
            Join the team behind
            <span className="font-editorial-italic copper-text">
              {" "}
              EELLEVEN MINING.
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-zinc-200 leading-relaxed font-light reveal reveal-delay-2">
            We are hiring drivers, loaders and offloading operators for our
            Kabwe yard. If you can move tonnes safely and
            on time, we want to hear from you.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3 reveal reveal-delay-3">
            <a
              href="#openings"
              data-testid="careers-cta-openings"
              className="px-7 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-xs hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center gap-3"
            >
              View Openings <ArrowRight size={14} />
            </a>
            <a
              href="#apply-form"
              data-testid="careers-cta-apply"
              className="px-7 py-4 border border-white/40 text-white font-semibold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors inline-flex items-center gap-3"
            >
              Apply Now
            </a>
          </div>
        </div>
      </section>

      {/* WHY WORK */}
      <section
        className="section-paper border-y border-[var(--eel-paper-2)]"
        data-testid="careers-why"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">Why work with us</p>
            <h2 className="font-editorial text-4xl md:text-5xl mt-5 leading-[1.05] text-[var(--eel-ink)]">
              Fair pay,
              <span className="font-editorial-italic copper-text">
                {" "}
                clean operations.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 text-[var(--eel-ink-muted)]">
            {[
              [
                "Competitive Pay",
                "Salary at or above sector benchmark, paid on schedule.",
              ],
              [
                "Safe Operations",
                "Modern fleet, PPE, safety briefings and incident-free culture.",
              ],
              [
                "Stable Hours",
                "Predictable shifts and proper rest in line with Zambian labour law.",
              ],
              [
                "Career Path",
                "Promotion from yard to supervisor and management roles.",
              ],
            ].map(([t, d], i) => (
              <div
                key={i}
                data-testid={`careers-why-${i}`}
                className="bg-white border border-[var(--eel-paper-2)] p-6"
              >
                <h3 className="font-editorial text-xl text-[var(--eel-ink)]">
                  {t}
                </h3>
                <p className="mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPENINGS */}
      <section
        id="openings"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 scroll-mt-24"
        data-testid="careers-openings"
      >
        <p className="label-eyebrow">Current openings</p>
        <h2 className="font-editorial mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
          Open roles at
          <span className="font-editorial-italic copper-text">
            {" "}
            our yard.
          </span>
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {JOBS.map((j) => {
            const Icon = j.icon;
            return (
              <div
                key={j.slug}
                data-testid={`careers-job-${j.slug}`}
                className="border border-[var(--eel-border)] p-7 flex flex-col"
              >
                <div className="flex items-baseline justify-between">
                  <Icon className="copper-text" size={28} />
                  <span className="label-eyebrow text-zinc-500">{j.code}</span>
                </div>
                <h3 className="mt-6 font-editorial text-2xl md:text-3xl text-white leading-tight">
                  {j.title}
                </h3>
                <p className="mt-2 label-eyebrow text-[var(--eel-copper)]">
                  {j.type}
                </p>
                <p className="mt-1 text-xs text-zinc-500">{j.location}</p>
                <p className="mt-5 text-sm text-zinc-400 leading-relaxed font-light">
                  {j.summary}
                </p>

                <div className="mt-6 border-t border-[var(--eel-border)] pt-5">
                  <p className="label-eyebrow text-zinc-500 mb-3">
                    Requirements
                  </p>
                  <ul className="space-y-2">
                    {j.requirements.map((r, i) => (
                      <li
                        key={i}
                        className="text-xs text-zinc-300 flex items-start gap-2 font-light"
                      >
                        <CheckCircle2
                          size={12}
                          className="copper-text mt-0.5 shrink-0"
                        />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 border-t border-[var(--eel-border)] pt-5">
                  <p className="label-eyebrow text-zinc-500 mb-3">
                    Responsibilities
                  </p>
                  <ul className="space-y-2">
                    {j.responsibilities.map((r, i) => (
                      <li
                        key={i}
                        className="text-xs text-zinc-300 flex items-start gap-2 font-light"
                      >
                        <span className="w-1 h-1 mt-1.5 copper-bg shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => apply(j.title)}
                  data-testid={`careers-apply-${j.slug}`}
                  className="mt-7 px-5 py-3 copper-bg text-black font-semibold uppercase tracking-widest text-[11px] hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center justify-center gap-3"
                >
                  Apply for this role <ArrowRight size={12} />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section
        id="apply-form"
        className="border-t border-[var(--eel-border)] bg-[var(--eel-surface)] scroll-mt-24"
        data-testid="careers-apply-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">Apply now</p>
            <h2 className="font-editorial mt-3 text-4xl md:text-5xl leading-[1.04]">
              Send your
              <span className="font-editorial-italic copper-text"> details.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed font-light">
              Tell us which role suits you, your years of experience and your
              preferred site. We&apos;ll contact qualifying applicants within 5
              working days.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="careers-contact-email"
                className="flex items-center gap-2 text-zinc-300 hover:text-[var(--eel-copper)]"
              >
                <Mail size={14} className="copper-text" />
                {COMPANY.email}
              </a>
              <a
                href={`https://wa.me/${COMPANY.phoneZambiaDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="careers-contact-wa-zm"
                className="flex items-center gap-2 text-zinc-300 hover:text-[var(--eel-copper)]"
              >
                <MessageCircle size={14} className="text-green-400" />
                WhatsApp Zambia · {COMPANY.phoneZambia}
              </a>
            </div>

            <Link
              to="/about"
              data-testid="careers-about-link"
              className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text link-underline"
            >
              About EELLEVEN <ArrowRight size={12} />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-[var(--eel-border)] p-6 md:p-10 bg-[var(--eel-bg)]">
              {submitted ? (
                <div className="py-12 text-center" data-testid="careers-success">
                  <CheckCircle2 size={56} className="copper-text mx-auto" />
                  <h3 className="font-editorial mt-6 text-3xl">
                    Application Received
                  </h3>
                  <p className="mt-3 text-zinc-400 max-w-md mx-auto font-light">
                    Thank you. Our HR team will contact qualifying applicants
                    within 5 working days.
                  </p>
                  <button
                    data-testid="careers-success-new"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 px-6 py-3 border border-[var(--eel-border-2)] text-sm uppercase tracking-widest hover:border-white"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="space-y-6"
                  data-testid="careers-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      id="name"
                      label="Full Name *"
                      value={form.name}
                      onChange={update("name")}
                      required
                      minLength={2}
                      placeholder="John Banda"
                    />
                    <Field
                      id="email"
                      type="email"
                      label="Email *"
                      value={form.email}
                      onChange={update("email")}
                      required
                      placeholder="john@example.com"
                    />
                    <Field
                      id="phone"
                      label="Phone / WhatsApp *"
                      value={form.phone}
                      onChange={update("phone")}
                      required
                      placeholder="+260 ..."
                    />
                    <Field
                      id="experience"
                      type="number"
                      label="Years of Experience"
                      value={form.experience}
                      onChange={update("experience")}
                      min={0}
                      placeholder="5"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="label-eyebrow text-zinc-400 block mb-2"
                        htmlFor="position"
                      >
                        Applying For *
                      </label>
                      <select
                        id="position"
                        data-testid="careers-input-position"
                        required
                        value={form.position}
                        onChange={update("position")}
                        className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 focus:outline-none"
                      >
                        <option value="">-- Select a role --</option>
                        {JOBS.map((j) => (
                          <option key={j.slug} value={j.title}>
                            {j.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        className="label-eyebrow text-zinc-400 block mb-2"
                        htmlFor="location"
                      >
                        Preferred Site
                      </label>
                      <select
                        id="location"
                        data-testid="careers-input-location"
                        value={form.location}
                        onChange={update("location")}
                        className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 focus:outline-none"
                      >
                        <option value="">-- Any site --</option>
                        <option value="Kabwe">Kabwe (HQ)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      className="label-eyebrow text-zinc-400 block mb-2"
                      htmlFor="message"
                    >
                      Brief Message *
                    </label>
                    <textarea
                      id="message"
                      data-testid="careers-input-message"
                      required
                      minLength={5}
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Briefly describe your experience and availability."
                      className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="careers-submit"
                    className="w-full md:w-auto px-8 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-xs hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting..." : (
                      <>
                        Submit Application <Send size={14} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, ...rest }) {
  return (
    <div>
      <label className="label-eyebrow text-zinc-400 block mb-2" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        data-testid={`careers-input-${id}`}
        {...rest}
        className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
      />
    </div>
  );
}
